import imageCompression from "browser-image-compression";

/**
 * Compress an image file client-side before upload.
 *
 * Goals:
 * - Target ~400 KB per product photo (originals often 3-8 MB from phones)
 * - Cap longest edge at 1600px (Retina-quality, plenty for zoom on any device)
 * - Keep original file format if PNG (transparency); otherwise convert to JPEG (smaller)
 * - Do NOT touch videos, PDFs, CSVs, or already-tiny files
 *
 * @param {File} file - the original file from an <input type=file> or drop event
 * @param {object} [opts] - overrides
 * @returns {Promise<{file: File, originalKB: number, compressedKB: number, savedPct: number}>}
 */
export async function compressImageIfNeeded(file, opts = {}) {
  const originalKB = Math.round(file.size / 1024);

  // Skip: non-images, small files (<500 KB), or unsupported formats
  if (!file.type.startsWith("image/")) {
    return { file, originalKB, compressedKB: originalKB, savedPct: 0, skipped: true };
  }
  if (file.size < 500 * 1024) {
    return { file, originalKB, compressedKB: originalKB, savedPct: 0, skipped: true };
  }
  if (file.type === "image/gif") {
    // GIFs animate — compressor would flatten. Leave as-is.
    return { file, originalKB, compressedKB: originalKB, savedPct: 0, skipped: true };
  }

  const options = {
    maxSizeMB: opts.maxSizeMB ?? 1.0, // target ~1 MB
    maxWidthOrHeight: opts.maxWidthOrHeight ?? 1600,
    useWebWorker: true,
    fileType: file.type === "image/png" ? "image/png" : "image/jpeg",
    initialQuality: opts.initialQuality ?? 0.90, // preserve fine fabric detail
  };

  try {
    const compressed = await imageCompression(file, options);
    // Preserve original filename with proper extension
    const ext = options.fileType === "image/png" ? "png" : "jpg";
    const baseName = (file.name.split(".").slice(0, -1).join(".") || file.name).replace(/[^a-z0-9-_]/gi, "_");
    const renamed = new File([compressed], `${baseName}.${ext}`, { type: options.fileType });

    const compressedKB = Math.round(renamed.size / 1024);
    const savedPct = Math.max(0, Math.round(((originalKB - compressedKB) / originalKB) * 100));
    return { file: renamed, originalKB, compressedKB, savedPct, skipped: false };
  } catch (e) {
    // If compression fails, fall back to original
    console.warn("Image compression failed, uploading original:", e);
    return { file, originalKB, compressedKB: originalKB, savedPct: 0, skipped: true };
  }
}
